import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { combineLatest, Observable, take } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { GeneralStore, GState } from './general.store';
import {
  AttributeDto,
  AttributeOperatorViewWithOperatorAttributeSettingsDto,
  OperatorAttributeDto,
} from '../../core/models/attribute-dto';
import { ErpDto } from '../../core/models/erp-dto';
import { OperatorDto } from '../../core/models/operator';
import { OrgUnitDto } from '../../core/models/org-unit-dto';
import { StockQuantityChangeReasonDto } from '../../core/models/stock-quantity-change-reason-dto';
import { UnitOfMeasureDto } from '../../core/models/unit-of-measure-dto';
import { UserDto } from '../../core/models/user-dto';
import { TreeNode } from '../../core/tree.utils';
import { isNullOrUndefined } from '../../core/utils';

@Injectable({ providedIn: 'root' })
export class GeneralQuery extends QueryEntity<GState> {
  loading$ = this.selectLoading();
  orgUnits$: Observable<TreeNode<OrgUnitDto>> = this.select('orgUnits');
  isOrgUnitRoot$: Observable<boolean> = this.select('orgUnits').pipe(map((data) => data?.children?.length === 0));
  attributes$: Observable<OperatorAttributeDto[]> = this.select('attributes');
  stockReductionReasons$: Observable<StockQuantityChangeReasonDto[]> = this.select('stockReductionReasons');
  operator$: Observable<OperatorDto> = this.select('operator');
  operatorUsers$: Observable<UserDto[]> = this.select('operatorUsers');
  erps$: Observable<ErpDto[]> = this.select('erps');
  unitOfMeasure$: Observable<UnitOfMeasureDto[]> = this.select('unitOfMeasure');
  currencies$: Observable<string[]> = this.select('currencies');
  hasCaAccess$: Observable<boolean> = this.select('hasCaAccess');
  hasSmAccess$: Observable<boolean> = this.select('hasSmAccess');
  hasPlanAccess$: Observable<boolean> = this.select('hasPlanAccess');
  hasPoolAccess$: Observable<boolean> = this.select('hasPoolAccess');
  isUserManager$: Observable<boolean> = this.select('isUserManager');
  isOperatorMultiErp$: Observable<boolean> = this.select('isOperatorMultiErp');
  catalogueAttributes$: Observable<AttributeDto[]> = this.select('catalogueAttributes');
  operatorAttributePermission$: Observable<AttributeOperatorViewWithOperatorAttributeSettingsDto[]> =
    this.select('operatorAttributePermission');
  hasUserAnyAccess$: Observable<boolean> = combineLatest([
    this.hasCaAccess$,
    this.hasSmAccess$,
    this.hasPlanAccess$,
    this.hasPoolAccess$,
  ]).pipe(
    map(
      ([hasCaAccess, hasSmAccess, hasPlanAccess, hasPoolAccess]: [boolean, boolean, boolean, boolean]) =>
        hasCaAccess || hasSmAccess || hasPlanAccess || hasPoolAccess,
    ),
  );
  columnSetupContext$ = combineLatest([this.isOperatorMultiErp$, this.catalogueAttributes$]).pipe(
    filter(([isMultiErp, attrs]) => !isNullOrUndefined(isMultiErp) && Array.isArray(attrs) && attrs.length > 0),
    map(([isMultiErp, attrs]) => ({ isMultiErp: !!isMultiErp, attrs }) as const),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  columnSetupContextOnce$ = this.columnSetupContext$.pipe(take(1));

  constructor(protected store: GeneralStore) {
    super(store);
  }

  getUOM(): UnitOfMeasureDto[] {
    return this.store.getValue().unitOfMeasure;
  }

  getOperator(): OperatorDto {
    return this.store.getValue().operator;
  }

  getOrgUnits(): TreeNode<OrgUnitDto> {
    return this.store.getValue().orgUnits;
  }

  getErps(): ErpDto[] {
    return this.store.getValue().erps;
  }

  isOperatorMultiErp(): boolean {
    return this.store.getValue().isOperatorMultiErp;
  }

  getOperatorLocationNameSizeLimit() {
    // if there is no operator data then we default to 10
    return this.store.getValue().operator?.locationNameSizeLimit ?? 10;
  }
}
